/**
 * Copyright.tsx — Copyright Notice
 * 
 * Reads copyright text from Magento's storeConfig (passed via context),
 * or from admin-configured data.text, or generates a sensible default.
 */
import React from 'react';

interface CopyrightProps {
  data: {
    text?: string;
    year?: number;
    secondaryText?: string;       // legal / VAT info shown on the second line in the two-line variant
    variant?: string;             // new
    content?: { variant?: string; secondaryText?: string };
    methods?: string[];           // payment marks shown alongside copyright in with-payment variant
    [key: string]: unknown;
  };
  styles?: string;
  variant?: string;
  context?: unknown;
}

const DEFAULT_PAYMENT_METHODS = ['Visa', 'Mastercard', 'Amex', 'PayPal'];

export const Copyright: React.FC<CopyrightProps> = ({ data, styles, variant, context }) => {
  const legacyVariant = variant;
  const isBentoDark = legacyVariant === 'bento-dark';

  // Priority: data.text (admin) → context.storeConfig.copyright (Magento) → auto.
  // Year MUST come from data.year (admin override) or context.year (set at SSR
  // time in Layout.astro frontmatter). NEVER fall back to new Date() at render
  // time — React renders this component on both server and client, and a
  // year boundary or timezone gap between the two produces a text-node
  // hydration mismatch (React #418). Final fallback is empty so SSR and
  // client always emit IDENTICAL strings.
  interface CopyrightContext { storeConfig?: { copyright?: string; vat_number?: string }; year?: number }
  const ctx = context as CopyrightContext | undefined;
  const resolvedYear = data?.year ?? ctx?.year ?? '';
  const copyrightText: string =
    data?.text ??
    ctx?.storeConfig?.copyright ??
    (resolvedYear ? `© ${resolvedYear} All rights reserved.` : '© All rights reserved.');

  // Variant: standard (default) | with-payment | two-line
  const variantRaw = data?.content?.variant ?? data?.variant ?? legacyVariant ?? 'standard';
  const layoutVariant: 'standard' | 'with-payment' | 'two-line' =
    ['standard','with-payment','two-line'].includes(variantRaw)
      ? variantRaw as 'standard' | 'with-payment' | 'two-line'
      : 'standard';

  const secondaryText = data?.content?.secondaryText ?? data?.secondaryText ?? ctx?.storeConfig?.vat_number ?? '';
  const paymentMethods = data?.methods && data.methods.length > 0 ? data.methods : DEFAULT_PAYMENT_METHODS;

  const textClass = isBentoDark
    // bento-dark gets the same chrome-scoped token, just a tighter mono
    // type treatment — was previously hardcoded text-slate-600 which
    // defeated theme customization for the dark-bento footer variant.
    ? 'text-[var(--color-footer-copyright-text,var(--text-muted))] font-mono text-[9px] uppercase tracking-widest'
    : 'text-[var(--color-footer-copyright-text, var(--text-muted, #64748b))]';

  const wrapperStyle = { backgroundColor: isBentoDark ? undefined : 'var(--color-footer-copyright-bg, transparent)' };

  // Variant: standard — single line copyright text.
  if (layoutVariant === 'standard') {
    return (
      <div className={styles ?? ''} style={wrapperStyle}>
        <p className={`text-sm ${textClass}`}>{copyrightText}</p>
      </div>
    );
  }

  // Variant: with-payment — copyright + payment marks inline.
  if (layoutVariant === 'with-payment') {
    return (
      <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${styles ?? ''}`} style={wrapperStyle}>
        <p className={`text-sm ${textClass}`}>{copyrightText}</p>
        <div className="flex items-center gap-1.5 flex-wrap">
          {paymentMethods.map(m => (
            <span
              key={m}
              className="px-2.5 py-1 rounded text-[9px] font-bold uppercase tracking-widest"
              style={{
                background: 'var(--style-chip-bg, var(--surface-2, transparent))',
                border: 'var(--style-chip-border, 1px solid var(--glass-border))',
                color: 'var(--color-footer-copyright-text, var(--text-muted))',
              }}
            >
              {m}
            </span>
          ))}
        </div>
      </div>
    );
  }

  // Variant: two-line — copyright on first line, company / VAT / legal on second.
  return (
    <div className={`flex flex-col gap-1 ${styles ?? ''}`} style={wrapperStyle}>
      <p className={`text-sm ${textClass}`}>{copyrightText}</p>
      {secondaryText && (
        <p className={`text-xs opacity-80 ${textClass}`}>{secondaryText}</p>
      )}
    </div>
  );
};

export default Copyright;
